/**
 * Auth state for the React app.
 *
 * Worth noticing what is NOT in this file: any token. The session lives in an
 * HttpOnly cookie that this code cannot read, by design — if JavaScript can
 * read it, so can an XSS payload. React only ever knows "who am I", never
 * "what is my credential". Module 8, Lesson 3.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api } from './api'

export interface User {
  id: string
  email: string
  displayName: string | null
  role: 'student' | 'admin' | 'owner'
}

export interface GameState {
  totalXp: number
  level: number
  xpIntoLevel: number
  xpForNextLevel: number
  progress: number
  streak: number
  longestStreak: number
  gems: number
  streakFreezes: number
}

interface MeResponse {
  user: User
  game: GameState
}

interface AuthValue {
  user: User | null
  game: GameState | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, displayName?: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  setGame: (g: GameState) => void
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [game, setGame] = useState<GameState | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const me = await api<MeResponse>('/auth/me')
      setUser(me.user)
      setGame(me.game)
    } catch {
      // A 401 here is not an error — it is the answer to "am I signed in?".
      setUser(null)
      setGame(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const login = useCallback(
    async (email: string, password: string) => {
      await api('/auth/login', { method: 'POST', body: { email, password } })
      await refresh()
    },
    [refresh],
  )

  const register = useCallback(
    async (email: string, password: string, displayName?: string) => {
      await api('/auth/register', { method: 'POST', body: { email, password, displayName } })
      await refresh()
    },
    [refresh],
  )

  const logout = useCallback(async () => {
    await api('/auth/logout', { method: 'POST' })
    setUser(null)
    setGame(null)
  }, [])

  const value = useMemo(
    () => ({ user, game, loading, login, register, logout, refresh, setGame }),
    [user, game, loading, login, register, logout, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.')
  return ctx
}
